import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import Table from './components/Table/Table';
import FilterBar from './components/FilterBar/FilterBar';
import OffCanvas from './components/OffCanvas/OffCanvas';
import Stats from './components/Stats/Stats';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { MPPageTitle } from '../../../../components/ui';

import { getPriceChangesByLocalId } from '../../../../crud/menu_api/price_changes';
import { PriceChange, ViewMode } from './types';
import { exportPriceChangesToExcel } from './utils/priceChangesExcelExport';
import { getUniqueUsers, getUniquePriceTypes } from './utils/priceChangeUtils';

interface FilterOption {
  value: any;
  label: string;
}

const PriceModifications: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewMode>('table');
  const [priceChanges, setPriceChanges] = useState<PriceChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilteredData, setSearchFilteredData] = useState<PriceChange[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const now = new Date();
  const ninetyDaysAgo = new Date(now);
  ninetyDaysAgo.setDate(now.getDate() - 90);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  
  const [startDate, setStartDate] = useState<string>(ninetyDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(tomorrow.toISOString().split('T')[0]);
  
  const [selectedUsers, setSelectedUsers] = useState<FilterOption[]>([]);
  const [selectedPriceTypes, setSelectedPriceTypes] = useState<FilterOption[]>([]);
  const [selectedChangeTypes, setSelectedChangeTypes] = useState<FilterOption[]>([]);
  
  const [userOptions, setUserOptions] = useState<FilterOption[]>([]);
  const [priceTypeOptions, setPriceTypeOptions] = useState<FilterOption[]>([]);
  const changeTypeOptions: FilterOption[] = [
    { value: 'increase', label: 'Aumentos' },
    { value: 'decrease', label: 'Disminuciones' }
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPriceChanges = async () => {
      setLoading(true);
      try {
        const userContext = localStorage.getItem('user');
        const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
        const response: any = await getPriceChangesByLocalId(localId, {
          start_date: startDate,
          end_date: endDate
        });
        const changes: PriceChange[] = response.data || [];
        setPriceChanges(changes);
        setUserOptions(getUniqueUsers(changes));
        setPriceTypeOptions(getUniquePriceTypes(changes));
      } catch (error) {
        console.error('Error fetching price changes:', error);
        toast.error('Error cargando cambios de precios');
        setPriceChanges([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPriceChanges();
  }, [startDate, endDate]);

  const filteredData = useMemo(() => {
    let data = [...priceChanges];
    if (selectedUsers.length > 0) {
      const userNames = selectedUsers.map(u => u.label);
      data = data.filter(change => userNames.includes(change.user_name));
    }
    if (selectedPriceTypes.length > 0) {
      const types = selectedPriceTypes.map(t => t.value);
      data = data.filter(change => types.includes(change.price_type));
    }
    if (selectedChangeTypes.length > 0) {
      const changeTypes = selectedChangeTypes.map(t => t.value);
      data = data.filter(change => {
        const isIncrease = change.new_price > change.old_price;
        if (changeTypes.includes('increase') && isIncrease) return true;
        if (changeTypes.includes('decrease') && !isIncrease) return true;
        return false;
      });
    }
    return data;
  }, [priceChanges, selectedUsers, selectedPriceTypes, selectedChangeTypes]);

  useEffect(() => {
    setSearchFilteredData(filteredData);
  }, [filteredData]);

  const handleExportToExcel = () => {
    const success = exportPriceChangesToExcel(searchFilteredData, startDate, endDate);
    if (success) {
      toast.success('Cambios de precios exportados exitosamente');
    } else {
      toast.error('No hay cambios de precios para exportar');
    }
  };

  const titles: Record<ViewMode, { main: string; sub: string }> = {
    table: {
      main: 'Historial de Cambios de Precios',
      sub: 'Lista de modificaciones de precios en el menú'
    },
    stats: {
      main: 'Estadísticas de Cambios de Precios',
      sub: 'Análisis de las modificaciones de precios'
    }
  };

  const { main, sub } = titles[currentView];

  return (
    <div className="flex flex-col gap-4 p-3 pb-6 lg:gap-5 lg:p-4 lg:pb-8">
      <MPPageTitle subtitle={sub}>{main}</MPPageTitle>

      <div className="mp-button-selector-page-container">
        <div className="mp-button-selector-page-selector">
          <button
            onClick={() => setCurrentView('table')}
            className={`mp-button-selector-page-button ${currentView === 'table' ? 'active' : 'inactive'}`}
          >
            Listado
          </button>
          <button
            onClick={() => setCurrentView('stats')}
            className={`mp-button-selector-page-button ${currentView === 'stats' ? 'active' : 'inactive'}`}
          >
            Estadísticas
          </button>
        </div>
      </div>

      {!isMobile && (
        <FilterBar
          searchFilteredData={filteredData}
          setSearchFilteredData={setSearchFilteredData}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          userOptions={userOptions}
          selectedUsers={selectedUsers}
          onUsersChange={setSelectedUsers}
          priceTypeOptions={priceTypeOptions}
          selectedPriceTypes={selectedPriceTypes}
          onPriceTypesChange={setSelectedPriceTypes}
          changeTypeOptions={changeTypeOptions}
          selectedChangeTypes={selectedChangeTypes}
          onChangeTypesChange={setSelectedChangeTypes}
          onExportToExcel={handleExportToExcel}
          totalChanges={searchFilteredData.length}
          currentView={currentView}
          loading={loading}
        />
      )}

      <OffCanvas
        show={showFilters}
        setShow={setShowFilters}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        userOptions={userOptions}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        priceTypeOptions={priceTypeOptions}
        selectedPriceTypes={selectedPriceTypes}
        setSelectedPriceTypes={setSelectedPriceTypes}
        changeTypeOptions={changeTypeOptions}
        selectedChangeTypes={selectedChangeTypes}
        setSelectedChangeTypes={setSelectedChangeTypes}
      />

      {loading ? (
        <LoadingSpinner />
      ) : currentView === 'stats' ? (
        <Stats data={filteredData} startDate={startDate} endDate={endDate} />
      ) : (
        <Table data={searchFilteredData} />
      )}
    </div>
  );
};

export default PriceModifications;
